import { tidyDisplayTitle } from 'src/utils/venueName'

const LONDON = 'Europe/London'

/** Titles the list shows as the same name, including a shared-with venue. */
export function recipeTitleKey(title) {
  const tidy = tidyDisplayTitle(title)
  return typeof tidy === 'string' ? tidy.toLowerCase() : ''
}

/** When the recipe was saved. Older recipes have no createdAt; the id carries it. */
export function recipeCreatedAt(recipe) {
  const explicit = recipe?.createdAt
  if (explicit) {
    const date = new Date(explicit)
    if (!Number.isNaN(date.getTime())) return date
  }
  const id = String(recipe?._id || '')
  if (/^[a-f0-9]{24}$/i.test(id)) {
    const seconds = parseInt(id.slice(0, 8), 16)
    if (Number.isFinite(seconds)) return new Date(seconds * 1000)
  }
  return null
}

const clockParts = (date, withSeconds) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23',
  }).formatToParts(date)

const part = (parts, type) =>
  parts.find(piece => piece.type === type)?.value || ''

const formatAdded = (date, { seconds }) => {
  if (!date) return null
  const pieces = clockParts(date, seconds)
  const clock = seconds
    ? `${part(pieces, 'hour')}:${part(pieces, 'minute')}:${part(
        pieces,
        'second',
      )}`
    : `${part(pieces, 'hour')}:${part(pieces, 'minute')}`
  return `Added ${part(pieces, 'day')} ${part(pieces, 'month')} ${part(
    pieces,
    'year',
  )}, ${clock}`
}

const stampKey = (date, withSeconds) => {
  if (!date) return 'unknown'
  const pieces = clockParts(date, true)
  const base = [
    part(pieces, 'year'),
    part(pieces, 'month'),
    part(pieces, 'day'),
    part(pieces, 'hour'),
    part(pieces, 'minute'),
  ]
  if (withSeconds) base.push(part(pieces, 'second'))
  return base.join('-')
}

const SAVED_PLACE = ['saved first', 'saved second', 'saved third']

/** Order within one minute. A two-second clock is too easy to miss. */
const savedPlace = index => SAVED_PLACE[index] || `saved ${index + 1}th`

/**
 * Cue for each recipe whose title is shared with another in this list.
 * A title that appears once has no cue. Times are UK. Copies saved in the
 * same minute are "saved first" and "saved second".
 * @returns {Map<string, string>}
 */
export function recipeDuplicateCues(recipes) {
  const groups = new Map()
  ;(recipes || []).forEach(recipe => {
    const key = recipeTitleKey(recipe?.title)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(recipe)
  })

  const cues = new Map()
  groups.forEach(group => {
    if (group.length < 2) return
    const stamped = group.map(recipe => ({
      recipe,
      at: recipeCreatedAt(recipe),
    }))
    const byMinute = new Map()
    stamped.forEach(row => {
      const minute = stampKey(row.at, false)
      if (!byMinute.has(minute)) byMinute.set(minute, [])
      byMinute.get(minute).push(row)
    })
    byMinute.forEach(rows => {
      const ordered = [...rows].sort((a, b) => {
        const delta = (a.at?.getTime() || 0) - (b.at?.getTime() || 0)
        if (delta !== 0) return delta
        return String(a.recipe?._id || '').localeCompare(
          String(b.recipe?._id || ''),
        )
      })
      const crowded = ordered.length > 1
      ordered.forEach((row, index) => {
        const id = String(row.recipe?._id || '')
        let cue = formatAdded(row.at, { seconds: false }) || 'Another copy'
        if (crowded) cue = `${cue} · ${savedPlace(index)}`
        if (id) cues.set(id, cue)
      })
    })
  })
  return cues
}

/** Archive confirm names the copy when the title is shared. */
export function recipeArchiveConfirmTitle(recipe, cue) {
  if (!cue) return 'Store this recipe?'
  const title = tidyDisplayTitle(recipe?.title) || 'this recipe'
  return `Store "${title}"? ${cue}`
}
